import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { Box, TableRow, Typography, Button } from '@mui/material';

import { useSidebar } from '../../context/SidebarContext';
import { useBreadcrumb } from '../../context/BreadcrumbContext';

import {
  HeaderRow,
  PageWrapper,
  PaginationFooter,
  StatusTag,
  StyledTable,
  StyledTableBody,
  StyledTableCell,
  StyledTableContainer,
} from './styles';

import CustomTableHeader from '../../components/CustomHeader';
import CustomPagination from '../../components/CustomPagination';
import PaginatedSearchSelect from '../../components/PaginatedSearchSelect';
import ActionPopup from '../../components/common/ActionPopup';
import { fetchFacilityList } from '../../modules/facility';
import CustomDateRangePicker from '../../components/CustomDateRangePicker';

import {
  fetchOrgInvoices,
  selectOrgInvoices,
  selectOrgInvoicesCount,
  selectInvoicesLoading,
  Invoice,
  generateOrgInvoice,
  publishInvoice,
  markInvoicePaid,
} from '../../modules/invoice';
import InvoiceDetailsModal from '../../components/InvoiceDetailsModal';



type Order = 'asc' | 'desc';

interface Column {
  id: string;
  label: string;
  numeric?: boolean;
  disableSorting?: boolean;
}

const INVOICE_COLUMNS: Column[] = [
  { id: 'invoice_number', label: 'Invoice Number' },
  { id: 'invoice_date', label: 'Invoice Date' },
  { id: 'invoice_due_date', label: 'Due Date' },
  { id: 'facility_name', label: 'Facility Name' },
  { id: 'period_start_from', label: 'Period From' },
  { id: 'period_end_to', label: 'Period To' },
  { id: 'invoice_amount', label: 'Amount' },
  { id: 'gst_applied', label: 'GST Applied' },
  { id: 'invoice_status', label: 'Status' },
  { id: 'payment_status', label: 'Payment Status' },
  { id: 'payment_action', label: 'Payment Action', disableSorting: true },
  { id: 'actions', label: 'Actions', disableSorting: true },
];

const OrgFacilityInvoices: React.FC = () => {
  const { setActivePage } = useSidebar();
  const { setPath } = useBreadcrumb();

  useEffect(() => {
    setActivePage('facility-invoices');
    setPath([
      { title: 'Facility Invoices', path: '/facility-invoices' },
    ]);
  }, [setActivePage, setPath]);

  const dispatch = useDispatch<any>();
  const invoices = useSelector(selectOrgInvoices);
  const invoicesCount = useSelector(selectOrgInvoicesCount);
  const invoicesLoading = useSelector(selectInvoicesLoading);
  // const facilityList = useSelector(selectFacilityList); // Removed direct selector usage

  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<string>('invoice_id');
  const [selectedFacility, setSelectedFacility] = useState<any>(null);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null]);

  const [openPublishPopup, setOpenPublishPopup] = useState(false);
  const [selectedInvoiceForPublish, setSelectedInvoiceForPublish] = useState<any>(null);

  const [openMarkPaidPopup, setOpenMarkPaidPopup] = useState(false);
  const [selectedInvoiceForMarkPaid, setSelectedInvoiceForMarkPaid] = useState<any>(null);

  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);

  const fetchFacilityOptions = async (query: string, page: number) => {
    try {
      const response = await dispatch(fetchFacilityList({
        page,
        pageSize: 10,
        search: query
      })).unwrap();

      const options = (response.results || []).map((f: any) => ({
        id: f.facility_id,
        label: f.facility_name,
        ...f
      }));

      // Add "All Facilities" option only on the first page and if searching doesn't restrict it heavily (optional, but good for UX)
      // Actually, standard pattern is usually just clearing the selection for "All".
      // But if we want an explicit option:
      if (page === 1 && !query) {
        options.unshift({ id: 'All', label: 'All Facilities' });
      }

      return {
        options,
        hasMore: response.next !== null
      };
    } catch (error) {
      console.error(error);
      return { options: [], hasMore: false };
    }
  };




  useEffect(() => {
    dispatch(
      fetchOrgInvoices({
        page,
        pageSize: rowsPerPage,
        facilityId: (selectedFacility && selectedFacility.id !== 'All') ? Number(selectedFacility.id) : undefined,
        startDate: dateRange[0]?.format('YYYY-MM-DD'),
        endDate: dateRange[1]?.format('YYYY-MM-DD'),
      }),
    );
  }, [dispatch, page, rowsPerPage, selectedFacility, dateRange]);

  const handleGenerateInvoice = async () => {
    if (!selectedFacility || selectedFacility.id === 'All') return;

    try {
      await dispatch(generateOrgInvoice({ facilityId: Number(selectedFacility.id) })).unwrap();
      // Refresh list after generation
      dispatch(
        fetchOrgInvoices({
          page,
          pageSize: rowsPerPage,
          facilityId: Number(selectedFacility.id),
        })
      );
    } catch (err) {
      console.error('Failed to generate invoice:', err);
    }
  };

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: string,
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const getInvoiceDataFromItems = (invoicesList: Invoice[]) => {
    if (!invoicesList) return [];
    // Return invoices directly, mapping facility name manually or handling it in render
    return invoicesList;
  }



  const invoiceItems = useMemo(() => {
    return getInvoiceDataFromItems(invoices || []);
  }, [invoices]);

  const handlePublishClick = (invoice: any) => {
    setSelectedInvoiceForPublish(invoice);
    setOpenPublishPopup(true);
  };

  const handlePublishConfirm = async () => {
    if (!selectedInvoiceForPublish) return;

    try {
      await dispatch(publishInvoice({
        invoiceId: selectedInvoiceForPublish.invoice_id,
        facilityId: selectedInvoiceForPublish.facility?.facility_id // Assuming facility object is nested
      })).unwrap();

      setOpenPublishPopup(false);
      setSelectedInvoiceForPublish(null);

      // Refresh list
      dispatch(
        fetchOrgInvoices({
          page,
          pageSize: rowsPerPage,
          facilityId: (selectedFacility && selectedFacility.id !== 'All') ? Number(selectedFacility.id) : undefined,
        })
      );
    } catch (err) {
      console.error('Failed to publish invoice:', err);
    }
  };



  const handleMarkPaidClick = (invoice: any) => {
    setSelectedInvoiceForMarkPaid(invoice);
    setOpenMarkPaidPopup(true);
  };

  const handleMarkPaidConfirm = async () => {
    if (!selectedInvoiceForMarkPaid) return;

    try {
      await dispatch(markInvoicePaid({
        invoiceId: selectedInvoiceForMarkPaid.invoice_id,
        facilityId: selectedInvoiceForMarkPaid.facility?.facility_id
      })).unwrap();

      setOpenMarkPaidPopup(false);
      setSelectedInvoiceForMarkPaid(null);

      // Refresh list
      dispatch(
        fetchOrgInvoices({
          page,
          pageSize: rowsPerPage,
          facilityId: (selectedFacility && selectedFacility.id !== 'All') ? Number(selectedFacility.id) : undefined,
        })
      );
    } catch (err) {
      console.error('Failed to mark invoice as paid:', err);
    }
  };



  const handleViewClick = (invoice: any) => {
    setSelectedInvoice(invoice);
    setOpenDetailsModal(true);
  };

  return (
    <PageWrapper>
      <Box sx={{ width: '100%', padding: 3 }}>
        <HeaderRow>
          <Typography
            variant="h5"
            style={{ fontWeight: 'bold', color: '#222' }}
          >
            Facility Invoices
          </Typography>
          <Box display="flex" gap={2} alignItems="center">
            <Box width="250px">
              <PaginatedSearchSelect
                label="Facility"
                fetchOptions={fetchFacilityOptions}
                getOptionLabel="label"
                value={selectedFacility}
                onChange={(value) => {
                  setSelectedFacility(value);
                  setPage(1);
                }}
              />
            </Box>
            <CustomDateRangePicker
              value={dateRange}
              onChange={(newDateRange) => setDateRange(newDateRange)}
            />
            <Button
              variant="contained"
              color="primary"
              disabled={!selectedFacility || selectedFacility.id === 'All' || invoicesLoading}
              onClick={handleGenerateInvoice}
              size="small"
              sx={{ height: '40px' }} // Match CustomSelect height roughly
            >
              Generate Invoice
            </Button>
          </Box>
        </HeaderRow>
        <StyledTableContainer>
          <StyledTable stickyHeader>
            <CustomTableHeader
              columns={INVOICE_COLUMNS}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <StyledTableBody>
              {invoicesLoading ? (
                <TableRow>
                  <StyledTableCell
                    colSpan={INVOICE_COLUMNS.length}
                    align="center"
                  >
                    Loading...
                  </StyledTableCell>
                </TableRow>
              ) : !invoiceItems || invoiceItems.length === 0 ? (
                <TableRow>
                  <StyledTableCell
                    colSpan={INVOICE_COLUMNS.length}
                    align="center"
                  >
                    No data available
                  </StyledTableCell>
                </TableRow>
              ) : (
                <>
                  {invoiceItems.map((invoice: any, index: number) => (
                    <TableRow key={`${invoice.invoice_number}-${index}`} hover>
                      <StyledTableCell>{invoice.invoice_number}</StyledTableCell>
                      <StyledTableCell>
                        {dayjs(invoice.invoice_date).format('DD/MM/YYYY')}
                      </StyledTableCell>
                      <StyledTableCell>
                        {dayjs(invoice.invoice_due_date).format('DD/MM/YYYY')}
                      </StyledTableCell>
                      <StyledTableCell>{invoice.facility?.facility_name || '-'}</StyledTableCell>
                      <StyledTableCell>
                        {dayjs(invoice.period_start_from).format('DD/MM/YYYY')}
                      </StyledTableCell>
                      <StyledTableCell>
                        {dayjs(invoice.period_end_to).format('DD/MM/YYYY')}
                      </StyledTableCell>
                      <StyledTableCell>{invoice.invoice_amount}</StyledTableCell>
                      <StyledTableCell>{invoice.gst_applied}</StyledTableCell>
                      <StyledTableCell>
                        <StatusTag status={invoice.invoice_status}>
                          {invoice.invoice_status}
                        </StatusTag>
                      </StyledTableCell>
                      <StyledTableCell>
                        <StatusTag status={invoice.payment_status}>
                          {invoice.payment_status}
                        </StatusTag>
                      </StyledTableCell>
                      <StyledTableCell>
                        {invoice.payment_status !== 'paid' && (
                          <Button
                            variant="outlined"
                            size="small"
                            color="success"
                            onClick={() => handleMarkPaidClick(invoice)}
                            disabled={!invoice.is_published}
                          >
                            Mark Paid
                          </Button>
                        )}
                      </StyledTableCell>
                      <StyledTableCell>
                        <Box display="flex" gap={1}>
                          {!invoice.is_published ? (
                            <Button
                              variant="outlined"
                              size="small"
                              color="primary"
                              onClick={() => handlePublishClick(invoice)}
                            >
                              Publish
                            </Button>
                          ) : (
                            <StatusTag status="Active">Published</StatusTag>
                          )}
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleViewClick(invoice)}
                          >
                            View
                          </Button>
                        </Box>
                      </StyledTableCell>
                    </TableRow>
                  ))}
                </>
              )}
            </StyledTableBody>
          </StyledTable>
        </StyledTableContainer>
        <PaginationFooter>
          <Box>
            Showing {invoices?.length || 0} of{' '}
            {invoicesCount} results
          </Box>
          <CustomPagination
            currentPage={page}
            totalPages={Math.ceil(invoicesCount / rowsPerPage)}
            onPageChange={(newPage) => setPage(newPage)}
          />
        </PaginationFooter>
        <ActionPopup
          open={openPublishPopup}
          onClose={() => setOpenPublishPopup(false)}
          onSubmit={handlePublishConfirm}
          title="Publish Invoice"
          message={`Are you sure you want to publish invoice ${selectedInvoiceForPublish?.invoice_number}? This action cannot be undone.`}
          submitButtonText="Publish"
          textFieldOptional={true} // No remark needed for now
        />
        <ActionPopup
          open={openMarkPaidPopup}
          onClose={() => setOpenMarkPaidPopup(false)}
          onSubmit={handleMarkPaidConfirm}
          title="Mark Invoice as Paid"
          message={`Are you sure you want to mark invoice ${selectedInvoiceForMarkPaid?.invoice_number} as PAID? This action cannot be undone.`}
          submitButtonText="Mark Paid"
          textFieldOptional={true}
        />
        <InvoiceDetailsModal
          open={openDetailsModal}
          onClose={() => setOpenDetailsModal(false)}
          invoice={selectedInvoice}
          loading={false}
          facilityDetails={selectedFacility ? {
            name: selectedFacility.facility_name || selectedFacility.label,
            address: selectedFacility.address || ''
          } : undefined}
        />
      </Box>
    </PageWrapper >
  );
};

export default OrgFacilityInvoices;
